import { useAuth } from '../../contexts/AuthContext';
import { NotificationCenter } from '../shared/NotificationCenter';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { LogOut, User as UserIcon, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const Topbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Sesion cerrada');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getInitials = (name) =>
    name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase() || 'U';

  return (
    <header
      className="bg-white border-b border-border px-4 md:px-6 flex items-center justify-between shrink-0"
      style={{ height: 'var(--topbar-height)' }}
      data-testid="topbar"
    >
      {/* Mobile: Clean brand header with isologo */}
      <div className="flex items-center gap-2 md:hidden">
        <img
          src="/isologo-azul.png"
          alt="Seguros Horizonte"
          className="h-8 object-contain"
        />
        <span className="font-bold text-sm text-[#052f6b] tracking-tight">Horizonte</span>
      </div>

      {/* Desktop: empty left space */}
      <div className="hidden md:block" />

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Notifications */}
        <NotificationCenter />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2.5 h-auto py-1 px-1.5 hover:bg-muted rounded-xl transition-colors outline-none"
              data-testid="user-menu-trigger"
            >
              <Avatar className="h-10 w-10 md:h-8 md:w-8 ring-2 ring-primary/15">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-primary text-primary-foreground text-sm md:text-xs font-bold">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:flex flex-col items-start">
                <p className="text-sm font-semibold text-foreground leading-tight">{user?.name}</p>
                <p className="text-[11px] text-muted-foreground">{user?.role}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground hidden sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="pb-3">
              <p className="text-sm font-bold">{user?.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{user?.email}</p>
              <span className="inline-flex mt-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-primary/10 text-primary">
                {user?.role}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate('/profile')}
              data-testid="profile-menu-item"
              className="cursor-pointer py-3 text-sm"
            >
              <UserIcon className="mr-3 h-5 w-5" />
              Mi Perfil
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="cursor-pointer text-destructive focus:text-destructive py-3 text-sm"
              data-testid="logout-menu-item"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Cerrar Sesion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
